import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radio, Calendar, Users, Video as VideoIcon, Sparkles, Clock, PlayCircle, Lock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Live & Conferences — unified landing page that shows every live stream,
 * every scheduled/upcoming stream, and every public conference room in one
 * place. "Start a stream" / "Host a meeting" CTAs are permission-gated: only
 * approved creators can start streams, and only creators (public meetings are
 * admin-only) can host conferences.
 */
export default function Live() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [live, setLive] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [l, u, m] = await Promise.all([
          api.get("/live").catch(() => ({ data: [] })),
          api.get("/live/upcoming").catch(() => ({ data: [] })),
          api.get("/meetings/public").catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        setLive(l.data || []);
        setUpcoming(u.data || []);
        setMeetings(m.data || []);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const canGoLive = user && ["creator", "premium_creator", "admin"].includes(user.role);
  const canHostMeeting = user && ["creator", "premium_creator", "admin"].includes(user.role);

  const totalViewers = useMemo(() => live.reduce((s, v) => s + (v.viewer_count || 0), 0), [live]);

  return (
    <div data-testid="live-landing-page" className="pt-32 pb-24 px-4 sm:px-6 md:px-8 max-w-7xl mx-auto">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> Live & Conferences</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-white mt-1 tracking-tighter">What's on right now</h1>
          <p className="text-white/60 mt-2 text-sm sm:text-base max-w-2xl">
            {live.length > 0 ? <>🔴 <b className="text-tv-live">{live.length}</b> streams live · <b>{totalViewers.toLocaleString()}</b> viewers watching · {upcoming.length} scheduled · {meetings.length} public meetings</> : <>No one's live this second — check upcoming streams + public meetings below.</>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canGoLive ? (
            <button onClick={() => nav("/studio/live")} data-testid="live-landing-go-live-btn" className="bg-tv-live hover:bg-red-600 text-white font-bold px-5 py-2.5 rounded-full flex items-center gap-2 shadow-lg shadow-red-500/20">
              <Radio className="w-4 h-4 animate-pulse" /> Go Live
            </button>
          ) : (
            <Link to="/apply-creator" data-testid="live-landing-apply-btn" className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-5 py-2.5 rounded-full flex items-center gap-2">
              <Lock className="w-4 h-4" /> Apply to stream
            </Link>
          )}
          {canHostMeeting ? (
            <button onClick={() => nav("/conference")} data-testid="live-landing-host-meeting-btn" className="bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-2.5 rounded-full flex items-center gap-2 border border-white/20">
              <VideoIcon className="w-4 h-4" /> Host a meeting
            </button>
          ) : (
            <Link to="/conference" data-testid="live-landing-meeting-browse-btn" className="bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-2.5 rounded-full flex items-center gap-2 border border-white/20">
              <VideoIcon className="w-4 h-4" /> Browse meetings
            </Link>
          )}
        </div>
      </header>

      {/* LIVE NOW */}
      <section data-testid="live-section" className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2.5 h-2.5 rounded-full bg-tv-live animate-pulse" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">On air now</h2>
          <span className="text-white/40 text-sm">{live.length}</span>
        </div>
        {loading ? (
          <div className="text-white/40">Loading…</div>
        ) : live.length === 0 ? (
          <div className="tv-glass rounded-2xl border border-white/10 p-8 text-center" data-testid="live-empty">
            <Radio className="w-8 h-8 text-white/30 mx-auto" />
            <div className="text-white/60 mt-2 text-sm">Nothing broadcasting right now. Scroll down for what's coming up.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {live.map((s) => (
              <Link
                key={s.id}
                to={`/live/${s.id}`}
                data-testid={`live-card-${s.id}`}
                className="tv-glass rounded-2xl border border-white/10 overflow-hidden hover:border-tv-live/60 transition group"
              >
                <div className="relative aspect-video bg-black">
                  <img src={s.thumbnail_url || `https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=800&q=80`} alt="" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition" />
                  <span className="absolute top-3 left-3 bg-tv-live text-white text-[10px] font-bold uppercase px-2 py-1 rounded flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                  </span>
                  {s.viewer_count > 0 && (
                    <span className="absolute top-3 right-3 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <Users className="w-3 h-3" /> {s.viewer_count}
                    </span>
                  )}
                  <PlayCircle className="absolute inset-0 m-auto w-12 h-12 text-white drop-shadow-2xl opacity-0 group-hover:opacity-100 transition" />
                </div>
                <div className="p-4">
                  <div className="font-display font-bold text-white text-base leading-tight truncate">{s.title}</div>
                  <div className="text-white/50 text-xs mt-1 truncate">{s.creator_name || "Creator"} · {(s.category || "live").replace(/_/g, " ")}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* UPCOMING */}
      <section data-testid="upcoming-section" className="mt-12">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-tv-accent" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">Upcoming streams</h2>
          <span className="text-white/40 text-sm">{upcoming.length}</span>
        </div>
        {upcoming.length === 0 ? (
          <div className="text-white/50 text-sm" data-testid="upcoming-empty">No scheduled streams yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcoming.map((s) => (
              <div key={s.id} data-testid={`upcoming-card-${s.id}`} className="tv-glass rounded-xl border border-white/10 p-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-tv-accent font-bold"><Calendar className="w-3 h-3" /> {s.scheduled_for ? new Date(s.scheduled_for).toLocaleString() : "Soon"}</div>
                <div className="font-display font-bold text-white text-base mt-1 leading-tight">{s.title}</div>
                <div className="text-white/50 text-xs mt-0.5 truncate">{s.creator_name || "Creator"} · {(s.category || "live").replace(/_/g, " ")}</div>
                {s.description && <div className="text-white/60 text-xs mt-2 line-clamp-2">{s.description}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* PUBLIC MEETINGS */}
      <section data-testid="meetings-section" className="mt-12">
        <div className="flex items-center gap-2 mb-4">
          <VideoIcon className="w-5 h-5 text-tv-brand" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">Public conference rooms</h2>
          <span className="text-white/40 text-sm">{meetings.length}</span>
        </div>
        {meetings.length === 0 ? (
          <div className="text-white/50 text-sm" data-testid="meetings-empty">No public meetings scheduled. Creators can host private meetings and invite attendees by email.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {meetings.map((m) => (
              <Link
                key={m.id}
                to={`/conference/join/${m.invite_token}`}
                data-testid={`meeting-card-${m.id}`}
                className="tv-glass rounded-xl border border-white/10 overflow-hidden hover:border-tv-brand/60 transition group"
              >
                {m.thumbnail_url && (
                  <div className="relative aspect-video bg-black">
                    <img src={m.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 bg-tv-brand text-white text-[10px] font-bold uppercase px-2 py-1 rounded flex items-center gap-1">
                      <VideoIcon className="w-3 h-3" /> Meeting
                    </span>
                  </div>
                )}
                <div className="p-4">
                  <div className="font-display font-bold text-white text-base leading-tight truncate">{m.title}</div>
                  <div className="text-white/50 text-xs mt-1 truncate">{m.host_name || "Host"} · {(m.kind || "meeting").replace(/_/g, " ")}</div>
                  <div className="text-[10px] uppercase tracking-wider text-tv-accent font-bold mt-2 flex items-center gap-1"><Users className="w-3 h-3" /> up to {m.max_participants} · {m.scheduled_for ? new Date(m.scheduled_for).toLocaleString() : "Join anytime"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Permission footnote */}
      <div className="mt-12 tv-glass rounded-xl border border-white/10 p-5 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-tv-accent shrink-0 mt-0.5" />
        <div className="text-white/70 text-sm">
          <b className="text-white">Who can start a stream?</b> Approved <span className="text-tv-accent">creator</span>, <span className="text-tv-accent">premium_creator</span>, and <span className="text-tv-accent">admin</span> accounts can start live streams and host private meetings. <Link to="/apply-creator" className="underline text-tv-brand hover:text-tv-accent">Apply for a creator account →</Link>
        </div>
      </div>
    </div>
  );
}
